import React from "react";
import { MuiFileInput } from "mui-file-input";
import InputWrapper from "./InputWrapper";
import { Controller } from "react-hook-form";
import { DEFAULT_SIZE, DEFAULT_ALLOWED_FILE_TYPES, FILE_TYPE_LABELS } from "../../../utils/constant";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReadOnlyInput from "./ReadOnlyInput";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material";

const UNSUPPORTED_FILE_TYPE_MESSAGE = `Invalid file format. Please upload a supported file type (${Object.values(FILE_TYPE_LABELS).join(", ")}).`;

const validateFileType = value => {
  if (!value) return true; // let a `required` rule (if any) handle the empty case
  const file = Array.isArray(value) ? value[0] : value;
  if (file && !DEFAULT_ALLOWED_FILE_TYPES.includes(file.type)) {
    return UNSUPPORTED_FILE_TYPE_MESSAGE;
  }
  return true;
};

export default function FileUploadField({ size = DEFAULT_SIZE, mode, label, isAddMode, control, name, inputProps, rules, fieldProps = {}, ...rest }) {
  const theme = useTheme();
  const { canView = false, canAdd = false, canEdit = false } = rest.permission || {};
  if (!canView || (isAddMode && !canAdd)) return null;
  const editModeAllowed = isAddMode ? canAdd : canEdit;
  if (mode === "read" || (mode === "edit" && !editModeAllowed)) return <ReadOnlyInput size={size} label={label} type="file" name={name} {...rest} rules={rules}/>;
  const selectedColor = theme.palette.inputs.fileUploadField.selectedColor;
  const existingValidate = rules?.validate;
  const mergedRules = {
    ...rules,
    validate:
      typeof existingValidate === "function"
        ? { parentValidate: existingValidate, fileType: validateFileType }
        : { ...(existingValidate || {}), fileType: validateFileType }
  };

  return (
    <InputWrapper size={size}>
      <Controller
        control={control}
        name={name}
        rules={mergedRules}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          return (
            <MuiFileInput
               label={
              <span>
                {label}
                {rules?.required && <span style={{ color: "red" }}> *</span>}
              </span>
            }
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error}
              helperText={error?.message || null}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              placeholder={label}
              InputProps={{
                inputProps: { accept: DEFAULT_ALLOWED_FILE_TYPES.join(","), ...(inputProps || {}) },
                startAdornment: <AttachFileIcon style={{ color: selectedColor }} />
              }}
              {...fieldProps}
            />
          );
        }}
      />
    </InputWrapper>
  );
}

FileUploadField.propTypes = {
  name: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["read", "edit"]).isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  isAddMode: PropTypes.bool,
  handleReadValueClick: PropTypes.func,
  size: PropTypes.string,
  fieldProps: PropTypes.object,
  inputProps: PropTypes.object,
  permission: PropTypes.shape({
    canView: PropTypes.bool,
    canAdd: PropTypes.bool,
    canEdit: PropTypes.bool
  })
};